import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, Target, ArrowRight } from 'lucide-react';
import { CareerGrowthChart } from '@/components/planner/career-growth-chart';
import { AICareerAdvisor } from '@/components/planner/ai-career-advisor';
import { RecommendedPaths } from '@/components/planner/recommended-paths';
import { PersonalityQuiz } from '@/components/planner/personality-quiz';
import { Link } from 'react-router-dom';

export default function PlannerPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [careerData, setCareerData] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [jobPreferences, setJobPreferences] = useState<any>(null);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCareerData();
      checkAssessmentStatus();
    }
  }, [user]);

  const fetchCareerData = async () => {
    if (!user) return;

    try {
      // First get the search results
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(
          `
          *,
          experiences (
            id,
            position,
            company_name,
            description,
            start_date,
            end_date,
            current
          ),
          education (
            id,
            degree,
            field_of_study
          )
        `,
        )
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profileData) {
        throw new Error('Profile not found');
      }

      setProfile(profileData);
      setJobPreferences(profileData.job_preferences);

      // Get current role from experiences
      const currentExperience =
        profileData.experiences?.find((exp: any) => exp.current) ||
        profileData.experiences?.[0];

      // Fetch active career path
      const { data: pathData, error: pathError } = await supabase
        .from('career_paths')
        .select('*')
        .eq('profile_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (pathError && pathError.code !== 'PGRST116') throw pathError;

      setCareerData({
        currentRole: currentExperience
          ? {
              title: currentExperience.position,
              company: currentExperience.company_name,
            }
          : null,
        targetRole: pathData
          ? {
              title: pathData.target_role,
              timeframe: pathData.timeframe,
            }
          : null,
        progress: pathData?.match_score || 0,
        milestones: pathData?.milestones || [
          'Complete advanced certifications',
          'Lead a major project',
          'Mentor junior team members',
        ],
      });
    } catch (error) {
      console.error('Error fetching career data:', error);
      // Set default career data on error
      setCareerData({
        currentRole: null,
        targetRole: null,
        progress: 0,
        milestones: [
          'Complete advanced certifications',
          'Lead a major project',
          'Mentor junior team members',
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  const checkAssessmentStatus = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from('personality_assessments')
        .select('status')
        .eq('user_id', user.id)
        .maybeSingle();

      setHasCompletedAssessment(data?.status === 'completed' || false);
    } catch (error) {
      console.error('Error checking assessment status:', error);
      setHasCompletedAssessment(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Career Planner</h1>
          <p className="text-muted-foreground">
            Plan your career path and set goals for your professional growth
          </p>
        </div>

        {/* Personality Assessment Section */}
        {hasCompletedAssessment ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-primary" />
                  <CardTitle>Personality Profile</CardTitle>
                </div>
                <Button asChild>
                  <Link to="/planner/results">
                    View Results
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                You've completed the personality assessment. View your detailed
                results to understand your professional traits and tendencies.
              </p>
            </CardContent>
          </Card>
        ) : (
          <PersonalityQuiz
            userId={user?.id}
            currentRole={careerData?.currentRole?.title}
            targetRole={careerData?.targetRole?.title}
          />
        )}

        {/* Career Growth Chart */}
        <CareerGrowthChart
          currentRole={careerData?.currentRole?.title}
          targetRole={careerData?.targetRole?.title}
          timeframe="2 years"
          milestones={careerData?.milestones}
          progress={careerData?.progress}
        />

        {/* AI Career Advisor */}
        <AICareerAdvisor
          currentRole={careerData?.currentRole?.title}
          targetRole={careerData?.targetRole?.title}
          profile={profile}
          jobPreferences={jobPreferences}
        />

        {/* Recommended Paths */}
        <RecommendedPaths paths={[]} />
      </div>
    </div>
  );
}